import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { OfferDay } from "@/domain/offer/rules";
import type { PartnerOffer } from "@/domain/offer/types";
import { czechPlural, occupancySentence, offerForms } from "@/lib/czech";
import { formatDate } from "@/lib/time";
import { OfferRow } from "./OfferRow";

interface OfferDayGroupProps {
  day: OfferDay;
  canTrade: boolean;
  highlightedOfferId: number | null;
  now: Date;
  onEdit: (offer: PartnerOffer) => void;
  onCancel: (offer: PartnerOffer) => void;
}

export function OfferDayGroup({
  day,
  canTrade,
  highlightedOfferId,
  now,
  onEdit,
  onCancel,
}: OfferDayGroupProps) {
  // A day the summary would otherwise leave collapsed still opens for the
  // offer a partner was sent here to find — collapsed-but-marked reads as lost.
  const holdsHighlighted = day.offers.some((offer) => offer.id === highlightedOfferId);
  const [isOpen, setIsOpen] = useState(day.expanded || holdsHighlighted);
  const count = day.offers.length;
  // An open day holding a single offer would only repeat the figure standing
  // right beneath it; the date stays because the heading is the collapse toggle.
  const isSummaryRedundant = isOpen && count === 1;
  const dayName = formatDate(day.date, "EEEE d. M.");
  const heading = isSummaryRedundant
    ? dayName
    : `${dayName} — ${count} ${czechPlural(count, offerForms)}, ${occupancySentence(
        day.occupied,
        day.capacity,
      )}`;

  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-2 text-left hover:text-primary"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <h2 className="font-display text-lg font-semibold tracking-tight first-letter:uppercase">
          {heading}
        </h2>
      </button>

      {isOpen && (
        <div className="space-y-4">
          {day.offers.map((offer) => (
            <OfferRow
              key={offer.id}
              offer={offer}
              canTrade={canTrade}
              isHighlighted={offer.id === highlightedOfferId}
              now={now}
              onEdit={onEdit}
              onCancel={onCancel}
              />
          ))}
        </div>
      )}
    </section>
  );
}
